import {atom, useSetAtom} from 'jotai';
import {useMemo, useRef} from 'react';
import {Future} from '~/library/types';
import {Countdown} from '../ui/ResendTimer';

export interface SetTimerActions {
    pending(): void;
    success(seconds?: number): void;
    failure(error: unknown): void;
}

export function useResendTimer(options?: {seconds?: number; attempts?: number}) {
    const seconds = options?.seconds ?? 60;
    const attempts = options?.attempts ?? Infinity;

    const state$ = useMemo(() => atom(Future.pending<Countdown>()), []);
    const setTimer = useSetAtom(state$);

    const timerActions: SetTimerActions = {
        pending() {
            stopTimerInterval();
            setTimer({pending: true});
        },
        success(s = seconds) {
            stopTimerInterval();
            startTimerInterval();
            setTimer({
                ok: true,
                value: {attempts, seconds: s, expiresAtMs: Date.now() + s * 1000},
            });
            decrementAttempts();
        },
        failure(error: unknown) {
            stopTimerInterval();
            setTimer({ok: false, error});
        },
    };

    const intervalRef = useRef(-1);

    function decrementAttempts() {
        setTimer(prev => {
            if (!prev.ok) return prev;
            return {
                ...prev,
                value: {...prev.value, attempts: prev.value.attempts - 1},
            };
        });
    }

    function stopTimerInterval() {
        window.clearInterval(intervalRef.current);
    }

    function startTimerInterval() {
        stopTimerInterval();
        intervalRef.current = window.setInterval(() => {
            setTimer(prev => {
                if (!prev.ok) return prev;
                let next = Math.ceil((prev.value.expiresAtMs - Date.now()) / 1000);
                if (next < 1) {
                    next = 0;
                    stopTimerInterval();
                }
                return {...prev, value: {...prev.value, seconds: next}};
            });
        }, 1000);
    }

    const timer$ = useMemo(() => atom(get => get(state$)), [state$]);

    return [timer$, timerActions] as const;
}
